import { Tooltip } from '@mantine/core';
import { NodeViewContent, NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { getThemeBorderRadiusStyle, getThemeBorderStyle } from 'shared/utils/theme.utils';
import { useGlobalTheme } from 'shared/utils/useGlobalTheme';

import classes from './Tooltip.module.scss';
import { TooltipConfig } from './TooltipConfig';
import { TooltipEditModal } from './TooltipEditModal';
import { RichText } from '../../../../../components/richTexts/RichText/RichText';
import { SideMenuPortalBase } from '../../../../../components/studio/SideMenuPortalBase/SideMenuPortalBase';
import { TooltipProps } from '../Tooltip.types';
import {
  DEFAULT_BORDER,
  DEFAULT_BORDER_RADIUS,
  DEFAULT_IS_BORDER_VISIBLE,
  DEFAULT_IS_SHADOW_VISIBLE,
  DEFAULT_OFFSET,
  DEFAULT_PADDING,
  DEFAULT_POSITION,
  DEFAULT_SHADOW
} from '../consts';

type TooltipNodeContextType = {
  className?: string;
  isEditable?: boolean;
  shouldHide?: boolean;
  withinPortal?: boolean;
};

export const TooltipNodeContext = createContext<TooltipNodeContextType | null>(null);

export function useTooltipNodeContext() {
  const ctx = useContext(TooltipNodeContext);
  if (ctx === null) throw new Error('TooltipNodeContext not initialized properly.');
  return ctx;
}

export function TooltipNode(props: NodeViewProps) {
  const {
    node: { attrs },
    updateAttributes,
    editor
  } = props;
  const [isOver, setIsOver] = useState(false);

  const [styles, setStyles] = useState<TooltipProps>(attrs.tooltipStyle);
  const theme = useGlobalTheme();

  const handleUpdateStyle = useCallback(
    (styleToUpdate: TooltipProps) => {
      setStyles(styles => ({ ...styles, ...styleToUpdate }));
    },
    [setStyles]
  );

  const handleRemoveStyle = useCallback(
    (styleKey: keyof TooltipProps) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [styleKey]: _, ...restStyles } = styles;
      setStyles(restStyles);
    },
    [styles, setStyles]
  );

  useEffect(() => {
    queueMicrotask(() => {
      // Required (https://github.com/ueberdosis/tiptap/issues/3580)
      updateAttributes({ tooltipStyle: styles });
    });
  }, [styles, updateAttributes]);

  const onCloseEditModal = useCallback(
    (content: string) => {
      const emptyContentRegex = /^\s*<p[^>]*><\/p>\s*$/;
      const isEmptyContent = !content || emptyContentRegex.test(content);
      if (isEmptyContent) {
        return editor.commands.removeTooltip(attrs.tooltipId);
      }
      return updateAttributes({ isEditMode: false, tooltipContent: content });
    },
    [attrs.tooltipId, editor.commands, updateAttributes]
  );

  const handleMouseEnter = useCallback(() => {
    setIsOver(true);
  }, [setIsOver]);

  const handleMouseLeave = useCallback(() => {
    setIsOver(false);
  }, [setIsOver]);

  const [isActive, setIsActive] = useState(false);
  const { shouldHide, isEditable, className, withinPortal = false } = useTooltipNodeContext();

  useEffect(() => {
    if (shouldHide) {
      setIsActive(false);
    }
  }, [shouldHide]);

  useEffect(() => {
    if (!isEditable) return;

    const handleUpdate = () => {
      const isActive = editor.isActive('tooltip', { tooltipId: attrs.tooltipId });
      setIsActive(isActive);
    };

    editor.on('selectionUpdate', handleUpdate);
    return () => {
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [attrs.tooltipId, editor, isEditable]);

  return (
    <NodeViewWrapper as="span" className="react-component-with-content">
      <Tooltip
        withArrow
        arrowSize={7}
        classNames={{ tooltip: classes.tooltipLabel }}
        className={className}
        bg={styles.backgroundColor ?? theme.other.colors.neutral.white}
        offset={styles.offset ?? DEFAULT_OFFSET}
        p={styles.padding ?? DEFAULT_PADDING}
        styles={{
          tooltip: {
            ...getThemeBorderRadiusStyle(DEFAULT_BORDER_RADIUS, styles.cornerRadius),
            ...(styles.isBorderVisible ?? DEFAULT_IS_BORDER_VISIBLE
              ? getThemeBorderStyle(DEFAULT_BORDER, '#a6a7ab', styles.border)
              : {}),
            boxShadow: styles.isShadowVisible ?? DEFAULT_IS_SHADOW_VISIBLE ? styles.shadow ?? DEFAULT_SHADOW : 'none'
          },
          arrow: {
            ...(styles.isBorderVisible ?? DEFAULT_IS_BORDER_VISIBLE
              ? getThemeBorderStyle(DEFAULT_BORDER, '#a6a7ab', styles.border)
              : {})
          }
        }}
        label={
          <div
            id={attrs.tooltipId}
            contentEditable={false}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseEnter}
          >
            <RichText dataTestIdPrefix="tooltip" editable={false} htmlContent={attrs.tooltipContent} />
          </div>
        }
        opened={isActive || isOver}
        position={styles.position ?? DEFAULT_POSITION}
        withinPortal={withinPortal}
        pos="fixed"
      >
        <span className={classes.tooltipTrigger} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <NodeViewContent as="span" className="content" aria-describedby={attrs.tooltipId} />
        </span>
      </Tooltip>

      {isEditable && isActive && (
        <SideMenuPortalBase>
          <TooltipConfig onRemoveStyle={handleRemoveStyle} styles={styles} onUpdateStyle={handleUpdateStyle} />
        </SideMenuPortalBase>
      )}

      {isEditable && attrs.isEditMode && <TooltipEditModal onClose={onCloseEditModal} content={attrs.tooltipContent} />}
    </NodeViewWrapper>
  );
}
